import { useDispatch } from "react-redux";
import { setApplicationRoutingData, setIsEsignTaskViewed, setIsLoanDetailsUpdateViewed, setProfileActiveTab } from "features/authSlice";
import { useLazyGetMyLoansByDealApplicationIdQuery } from "pages/application/applicationService";
import Notifications, { NotificationProps, NotificationsProps } from "./Notifications";
import { NotificationType } from "pages/authentication/userService";
import { NavigationIds } from "../shared-navigation/SharedNavigation";

export interface DynamicNotificationProps extends NotificationProps {
  readonly userId: string;
  readonly nhDealApplicationId: string;
}

export interface DynamicNotificationsHandlerProps extends Omit<NotificationsProps, "onNotificationClick" | "notifications"> {
  readonly notifications?: DynamicNotificationProps[];
  readonly onNotificationClick?: (id: string) => void;
}

export default function DynamicNotificationsHandler({
  notifications,
  onNotificationClick,
  onDocumentNotificationClick,
  isOpen = true,
  onClose,
  triggerElementSelector = ".global-header__notification-button",
  autoCloseOnClick = true,
}: DynamicNotificationsHandlerProps) {
  const dispatch = useDispatch();
  const [fetchMyLoansByDealApplicationId] = useLazyGetMyLoansByDealApplicationIdQuery();

  const handleNotificationClick = async (notification: DynamicNotificationProps) => {
    const { id, userId, nhDealApplicationId, palInstanceId, notificationType } = notification;
    onNotificationClick?.(id);

    switch (notificationType) {
      case NotificationType.Esign:
        dispatch(setIsEsignTaskViewed(true));
        dispatch(setProfileActiveTab(NavigationIds.Tasks));
        return;
      case NotificationType.LoanDetails:
        dispatch(setIsLoanDetailsUpdateViewed(true));
        dispatch(setProfileActiveTab(NavigationIds.LoanDetails));
        return;
      default:
        break;
    }

    try {
      const response = await fetchMyLoansByDealApplicationId({ userId, nhDealApplicationId }).unwrap();
      if (response?.length) {
        const data = response[0];
        dispatch(
          setApplicationRoutingData({
            nhDealApplicationId: data.nhDealApplicationId,
            nhApplicationId: data.nhDealApplicationId,
            applicationRoleType: data.applicationRoleType,
            dealApplicationId: data.dealApplicationId,
            nhApplicationStatus: data.status,
            loanId: data.loanId,
            hasPal: data.hasPal,
            prospectId: data.prospectId,
            loanPurpose: data.loanPurpose,
            leadProvider: data.leadProvider,
            isConsentCollected: data.isConsentCollected,
            userRole: data.userRole,
            inquiryId: data.inquiryId,
            coBorrowerHasOwnNhApplication: data.coBorrowerHasOwnNhApplication,
            nhApplicationPath: data.nhApplicationPath,
          })
        );
      }

      // Trigger document navigation
      onDocumentNotificationClick?.(palInstanceId || id);
    } catch (error) {
      console.error("Error fetching deal application data:", error);
    }
  };

  const dynamicNotifications = notifications?.map((notification) => {
    const { userId, nhDealApplicationId, ...baseNotification } = notification;
    return {
      ...baseNotification,
      onNotificationClick: () => handleNotificationClick(notification),
    };
  });

  return (
    <Notifications
      notifications={dynamicNotifications}
      onNotificationClick={onNotificationClick}
      onDocumentNotificationClick={onDocumentNotificationClick}
      isOpen={isOpen}
      onClose={onClose}
      triggerElementSelector={triggerElementSelector}
      autoCloseOnClick={autoCloseOnClick}
    />
  );
}
